/**
 * New notifications arrive as an Apple-style stack: frosted cards in the top
 * corner (top of the screen on phones), grouped by kind. A group shows its
 * newest card with the others tucked behind; tap it to fan them out, swipe a
 * card sideways (or ✕) to clear it. Each arrival plays the Eaze chime once.
 * When the tab is in the background and the person allowed it, the same
 * notification is shown by the phone / computer (they stack it there too).
 *
 * Fed by the bell's notifications query (useNotifications); this only decides
 * what is new since the page opened and how it's shown. Nothing here changes
 * what's stored — tapping a card marks it read, like the bell.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion, type PanInfo } from "framer-motion";
import { X, ChevronUp } from "lucide-react";
import { useNotifications, useMarkNotificationRead } from "@/hooks/useNotifications";
import type { Notification } from "@/services/notificationService";
import { playEazeSound, unlockSoundOnFirstGesture } from "@/lib/eaze-sound";
import { showSystemNotification } from "@/lib/system-notify";

const GROUP_LABEL: Record<string, string> = {
  order: "Sales",
  inventory: "Stock",
  report: "Reports",
  hr: "People",
  approval: "Approvals",
  projects: "Projects",
  system: "Eaze",
};
const SHOW_MS = 9000; // a group tucks itself away after this, unless touched
const MAX_GROUPS = 3;

type Item = Notification & { arrivedAt: number };
type Group = { key: string; label: string; items: Item[]; touched: boolean };

const groupOf = (n: Notification) => String(n.metadata?.kind === "approval_request" ? "approval" : n.metadata?.kind === "project_portal" ? "projects" : n.category || "system");
const ago = (iso: string) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 45) return "now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
};

function AppMark() {
  return (
    <span aria-hidden className="grid h-5 w-5 normal-case place-items-center rounded-[6px] bg-gradient-to-br from-emerald-500 to-emerald-700 text-[11px] font-bold leading-none text-white shadow-sm">
      e
    </span>
  );
}

function Card({ n, label, onOpen, onClear, compact }: { n: Item; label: string; onOpen: () => void; onClear: () => void; compact?: boolean }) {
  const reduce = useReducedMotion();
  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (Math.abs(info.offset.x) > 90 || Math.abs(info.velocity.x) > 600) onClear();
  };
  return (
    <motion.div
      layout={!reduce}
      drag={reduce ? false : "x"}
      dragSnapToOrigin
      dragElastic={0.4}
      onDragEnd={onDragEnd}
      initial={reduce ? false : { opacity: 0, y: -16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, x: 120, transition: { duration: 0.18 } }}
      transition={{ type: "spring", stiffness: 420, damping: 34 }}
      className="group relative w-full cursor-pointer select-none rounded-2xl border border-white/50 bg-white/75 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-xl dark:border-white/10 dark:bg-zinc-900/75"
      role="button"
      tabIndex={0}
      aria-label={`${n.title}. ${n.message}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
        if (e.key === "Delete" || e.key === "Backspace") onClear();
      }}
      data-testid="stack-card"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
        <AppMark />
        <span className="font-medium">Eaze · {label}</span>
        <span className="ml-auto normal-case tracking-normal">{ago(n.createdAt)}</span>
        <button
          type="button"
          aria-label="Clear"
          className="-mr-1 rounded-full p-0.5 opacity-0 transition-opacity hover:bg-black/5 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-1.5 text-sm font-semibold leading-snug text-foreground">{n.title}</div>
      <div className={`text-sm leading-snug text-foreground/80 ${compact ? "line-clamp-1" : "line-clamp-2"}`}>{n.message}</div>
    </motion.div>
  );
}

function StackGroup({ g, onOpen, onClear, onClearGroup, onTouch }: { g: Group; onOpen: (n: Item) => void; onClear: (id: string) => void; onClearGroup: () => void; onTouch: () => void }) {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();
  const [front, ...rest] = g.items;
  if (!front) return null;
  const many = g.items.length > 1;

  if (open && many) {
    return (
      <motion.div layout={!reduce} className="space-y-2" onPointerEnter={onTouch}>
        <div className="flex items-center justify-between px-1 text-xs font-medium text-white drop-shadow sm:text-foreground sm:drop-shadow-none">
          <span>{g.label}</span>
          <span className="flex gap-1">
            <button type="button" className="flex items-center gap-1 rounded-full bg-black/30 px-2 py-0.5 backdrop-blur sm:bg-muted" onClick={() => setOpen(false)}>
              <ChevronUp className="h-3 w-3" /> Show less
            </button>
            <button type="button" aria-label={`Clear ${g.label}`} className="rounded-full bg-black/30 p-1 backdrop-blur sm:bg-muted" onClick={onClearGroup}>
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
        <AnimatePresence initial={false}>
          {g.items.map((n) => (
            <Card key={n._id} n={n} label={g.label} onOpen={() => onOpen(n)} onClear={() => onClear(n._id)} />
          ))}
        </AnimatePresence>
      </motion.div>
    );
  }

  return (
    <motion.div layout={!reduce} className="relative" onPointerEnter={onTouch} data-testid={`stack-group-${g.key}`}>
      <div className="relative">
        {/* the cards tucked behind, peeking out underneath like on a phone */}
        {rest.slice(0, 2).map((n, i) => (
          <div
            key={n._id}
            aria-hidden
            className="absolute rounded-b-2xl border border-t-0 border-white/40 bg-white/55 shadow-md backdrop-blur-xl dark:border-white/10 dark:bg-zinc-900/55"
            style={{ left: `${(i + 1) * 10}px`, right: `${(i + 1) * 10}px`, bottom: `-${(i + 1) * 8}px`, height: "24px", zIndex: -1 - i, opacity: 1 - i * 0.3 }}
          />
        ))}
        <div
          onClickCapture={
            many
              ? (e) => {
                  if ((e.target as HTMLElement).closest('button[aria-label="Clear"]')) return; // ✕ clears the group
                  e.stopPropagation();
                  setOpen(true);
                }
              : undefined
          }
        >
          <Card n={front} label={g.label} onOpen={() => onOpen(front)} onClear={many ? onClearGroup : () => onClear(front._id)} compact={many} />
        </div>
      </div>
      {many && (
        <button type="button" className="mt-5 w-full text-center text-[11px] font-medium text-white drop-shadow sm:text-muted-foreground sm:drop-shadow-none" onClick={() => setOpen(true)}>
          {g.items.length - 1} more from {g.label}
        </button>
      )}
    </motion.div>
  );
}

export default function NotificationStack() {
  const { data } = useNotifications({ limit: 20 });
  const markRead = useMarkNotificationRead();
  const navigate = useNavigate();
  const seen = useRef<Set<string> | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [touched, setTouched] = useState<Record<string, number>>({});

  useEffect(() => unlockSoundOnFirstGesture(), []);

  // What's new since this page opened (the first answer is just "what was there").
  useEffect(() => {
    const list = data?.notifications;
    if (!list) return;
    if (!seen.current) {
      seen.current = new Set(list.map((n) => n._id));
      return;
    }
    const fresh = list.filter((n) => !n.read && !seen.current!.has(n._id));
    if (!fresh.length) return;
    fresh.forEach((n) => seen.current!.add(n._id));
    const now = Date.now();
    setItems((cur) => [...fresh.map((n) => ({ ...n, arrivedAt: now })), ...cur].slice(0, 30));
    const urgent = fresh.some((n) => groupOf(n) === "approval" || n.type === "warning" || n.type === "error");
    if (document.visibilityState === "hidden") fresh.slice(0, 3).forEach((n) => showSystemNotification({ title: n.title, body: n.message, tag: `eaze-${n._id}`, url: n.link || "/" }));
    playEazeSound(urgent ? "attention" : "notify");
  }, [data]);

  // Groups tuck themselves away after a while unless the person is looking at them.
  useEffect(() => {
    if (!items.length) return;
    const t = setInterval(() => {
      const now = Date.now();
      setItems((cur) => cur.filter((n) => now - Math.max(n.arrivedAt, touched[groupOf(n)] || 0) < SHOW_MS));
    }, 1000);
    return () => clearInterval(t);
  }, [items.length, touched]);

  const groups = useMemo<Group[]>(() => {
    const m = new Map<string, Group>();
    for (const n of items) {
      const k = groupOf(n);
      if (!m.has(k)) m.set(k, { key: k, label: GROUP_LABEL[k] || "Eaze", items: [], touched: false });
      m.get(k)!.items.push(n);
    }
    return [...m.values()].slice(0, MAX_GROUPS);
  }, [items]);

  const clear = (id: string) => setItems((cur) => cur.filter((n) => n._id !== id));
  const clearGroup = (k: string) => setItems((cur) => cur.filter((n) => groupOf(n) !== k));
  const open = (n: Item) => {
    clear(n._id);
    if (!n.read) markRead.mutate(n._id);
    if (n.link) navigate(n.link);
  };

  return (
    <div
      aria-live="polite"
      aria-label="New notifications"
      className="pointer-events-none fixed inset-x-2 top-2 z-[60] flex flex-col gap-3 sm:inset-x-auto sm:right-4 sm:top-16 sm:w-[360px]"
    >
      <AnimatePresence initial={false}>
        {groups.map((g) => (
          <motion.div key={g.key} className="pointer-events-auto" exit={{ opacity: 0, transition: { duration: 0.15 } }}>
            <StackGroup g={g} onOpen={open} onClear={clear} onClearGroup={() => clearGroup(g.key)} onTouch={() => setTouched((t) => ({ ...t, [g.key]: Date.now() }))} />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
